import React, { Fragment, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators

import { changeData } from '@actions/ssap/ssapActionCreators';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { dataSyncingTools } from '@utils/tools/dataSyncingTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '@utils/tools/preventerTools';
import { getDateDifference } from '@utils/dates/date.utils';

// Constants

import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';




/**
 *
 * @param {Object} props
 * @returns {React.Component} - Full Time Student Page
 *
 */
const Page2_27 = props => {

    const {
        data,
        data: {
            taxHousehold: {
                thCommonParams: {
                    hasFullTimeStudent
                }
            }
        },
        environment: {
            localDates: {
                serverDate
            }
        },
        form,
        formName,
        householdMembers = []
    } = props;

    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const onFieldChange_member = (newData, indexInArray) => {
        const newState = { ...data };

        newState
            .taxHousehold
            .householdMember[indexInArray] = newData;

        const syncedData = dataSyncingTools.syncThCommonProps(
            newState,
            { propName: 'student' }
        );

        props.actions.changeData(syncedData);
    };

    const isPotentialFullTimeStudent = hhm => {
        const age = getDateDifference(serverDate, hhm.dateOfBirth, 'years');
        return age >= 18 && age <= 22;
    };

    const onFieldChange_none_of_above = (...args) => {

        const [ newData ] = args;

        const updateIndividualValues = hhm => {
            return isPotentialFullTimeStudent(hhm)
                ? {
                    ...hhm,
                    fullTimeStudent: false
                }
                : {
                    ...hhm,
                    fullTimeStudent: null
                };
        };

        const syncedData = dataSyncingTools.syncThCommonProps(
            newData,
            {
                customUpdater: updateIndividualValues,
                isCommon: true,
                propName: 'student'
            }
        );

        props.actions.changeData(syncedData);
    };

    const getHouseholdMembers = (householdMembers) => {

        const options = householdMembers
            .filter(isPotentialFullTimeStudent)
            .reduce((options, hhm) => {

                const {
                    applicantGuid,
                    name,
                    fullTimeStudent,
                    personId
                } = hhm;

                return [
                    <li key = { applicantGuid } >
                        <UniversalInput
                            label = { {
                                text: 'common.input.label.non_internationalized_text',
                                labelTextReplacements: {
                                    text: normalizeNameOfPerson(name)
                                }
                            } }
                            inputData = { {
                                isInputRequired: false,
                                type: INPUT_TYPES.CHECKBOX,
                                name: 'fullTimeStudent',
                                checked: fullTimeStudent,
                                value: fullTimeStudent,
                                currentData: cloneObject(hhm),
                            } }
                            inputActions = { {
                                onFieldChange: onFieldChange_member
                            } }
                            indexInArray = { personId - 1 }
                            errorClasses = { [ 'margin-l-0' ] }
                        />
                    </li>,
                    ...options
                ];
            }, [
                <li key = 'None_of_above' >
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.none_of_above',
                            isLabelRequired: false
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.CHECKBOX,
                            name: 'hasFullTimeStudent',
                            checked: hasFullTimeStudent === false,
                            value: hasFullTimeStudent === false,
                            currentData: cloneObject(data),
                            fields: [ 'taxHousehold', 'thCommonParams' ],
                            reversedValue: true
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange_none_of_above
                        } }
                    />
                </li>
            ]);
        
        return (
            <ul className="usa-list usa-list--unstyled">
                { options }
            </ul>
        );
    };

    const topLevelValidity = useMemo(
        () => {
            return householdMembers.some(hhm => hhm.fullTimeStudent) || hasFullTimeStudent === false;
        },
        [ householdMembers, hasFullTimeStudent ]
    );

    return (
        <Fragment>
            <div
                className = 'fade-in'
                id = 'fhMoreInfo'
            >
                <div className = 'subsection' >
                    <Form
                        { ...{
                            formName,
                            topLevelValidity
                        } }
                    >
                        <p className = 'required-true'>
                            <FormattedMessage id = 'ssap.family_household.are_students' />
                        </p>
                        <ValidationErrorMessage
                            isVisible = { isFormSubmitted && !topLevelValidity }
                            messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                            errorClasses = { [ 'margin-l-0' ] }
                        />
                        {
                            getHouseholdMembers(householdMembers)
                        }
                    </Form>
                </div>
            </div>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    data: state.ssap.data,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_27);
